import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { toast } from 'sonner';
import {
   DELETE_ALL_APPLICATIONS,
   DELETE_ALL_PERSONNEL_NOTIFICATIONS,
   DELETE_ALL_USER_NOTIFICATIONS,
   RESET_ALL_APPLICATIONS,
   RESET_APPLICATION_BY_NUMBER,
   RESET_DATABASE,
   UNSUBMIT_ALL_APPLICATIONS
} from '../graphql/mutations';
import { GET_ALL_APPLICATION_NUMBERS } from '../graphql/queries';

export const useResetOptions = () => {
   const [searchTerm, setSearchTerm] = useState('');
   const [isResettingSpecific, setIsResettingSpecific] = useState(false);

   const { data: applicationNumbersData, loading: applicationNumbersLoading } =
      useQuery(GET_ALL_APPLICATION_NUMBERS);

   const [resetAllApplications] = useMutation(RESET_ALL_APPLICATIONS);
   const [resetApplicationByNumber] = useMutation(RESET_APPLICATION_BY_NUMBER);
   const [unsubmitAllApplications] = useMutation(UNSUBMIT_ALL_APPLICATIONS);
   const [resetDatabase] = useMutation(RESET_DATABASE);
   const [deleteAllApplications] = useMutation(DELETE_ALL_APPLICATIONS);
   const [deleteAllUserNotifications] = useMutation(DELETE_ALL_USER_NOTIFICATIONS);
   const [deleteAllPersonnelNotifications] = useMutation(DELETE_ALL_PERSONNEL_NOTIFICATIONS);

   const filteredApplications = applicationNumbersData?.getAllApplicationDetails.filter(app =>
      app.applicationNumber.toLowerCase().includes(searchTerm.toLowerCase())
   ).map(app => app.applicationNumber) || [];

   const handleResetSpecific = async (appNumber) => {
      try {
         setIsResettingSpecific(true);
         const { data } = await resetApplicationByNumber({
            variables: { applicationNumber: appNumber }
         });

         if (data?.resetApplicationByNumber) {
            toast.success('Application has been reset successfully');
         }
      } catch (error) {
         console.error('Error resetting application:', error);
         toast.error('Failed to reset application');
      } finally {
         setIsResettingSpecific(false);
      }
   };

   const handleResetDatabase = async () => {
      try {
         const { data } = await resetDatabase();
         if (data?.resetDatabase?.success) {
            toast.success('Database reset successfully');
         }
      } catch (error) {
         console.error('Error resetting database:', error);
         toast.error('Failed to reset database');
      }
   };

   const handleDeleteAllApplications = async () => {
      try {
         const { data } = await deleteAllApplications();
         if (data?.deleteAllApplications?.success) {
            toast.success(data.deleteAllApplications.message, {
               description: `${data.deleteAllApplications.count} applications deleted`
            });
         }
      } catch (error) {
         console.error('Error deleting all applications:', error);
         toast.error('Failed to delete all applications');
      }
   };

   const handleDeleteAllUserNotifications = async () => {
      try {
         const { data } = await deleteAllUserNotifications();
         if (data?.deleteAllUserNotifications?.success) {
            toast.success(data.deleteAllUserNotifications.message, {
               description: `${data.deleteAllUserNotifications.count} user notifications deleted`
            });
         }
      } catch (error) {
         console.error('Error deleting user notifications:', error);
         toast.error('Failed to delete user notifications');
      }
   };

   const handleDeleteAllPersonnelNotifications = async () => {
      try {
         const { data } = await deleteAllPersonnelNotifications();
         if (data?.deleteAllPersonnelNotifications?.success) {
            toast.success(data.deleteAllPersonnelNotifications.message, {
               description: `${data.deleteAllPersonnelNotifications.count} personnel notifications deleted`
            });
         }
      } catch (error) {
         console.error('Error deleting personnel notifications:', error);
         toast.error('Failed to delete personnel notifications');
      }
   };

   return {
      searchTerm,
      setSearchTerm,
      isResettingSpecific,
      applicationNumbersLoading,
      filteredApplications,
      handleResetSpecific,
      resetAllApplications,
      unsubmitAllApplications,
      handleResetDatabase,
      handleDeleteAllApplications,
      handleDeleteAllUserNotifications,
      handleDeleteAllPersonnelNotifications
   };
};